import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { MapPin, Phone, Mail, Instagram, Youtube, Twitter, ArrowRight } from "lucide-react";
import { HOME } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const footerLinks = {
  "Properties": [
    { label: "Luxury Apartments", href: "/properties?tag=Luxury" },
    { label: "Investor Picks", href: "/properties?tag=Investor+Picks" },
    { label: "Pre-Launch", href: "/properties?tag=Pre-launch" },
    { label: "Villas", href: "/properties?tag=Villas" },
    { label: "Commercial", href: "/properties?tag=Commercial" },
  ],
  "Resources": [
    { label: "Investment Insights", href: "/insights" },
    { label: "Why Navi Mumbai", href: "/why-navi-mumbai" },
    { label: "ROI Calculator", href: "/#roi-calculator" },
    { label: "Developers", href: "/developers" },
    { label: "AI Property Match", href: "/#ai-match" },
  ],
  "Company": [
    { label: "About Us", href: "/about" },
    { label: "Contact", href: "/contact" },
    { label: "Book Consultation", href: "/consultation" },
    { label: "Careers", href: "/contact" },
    { label: "Privacy Policy", href: "/contact" },
  ],
};

const Footer = () => {
  const [email, setEmail] = useState("");
  const [subStatus, setSubStatus] = useState(null);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      const res = await axios.post(`${API}/newsletter`, { email });
      setSubStatus(res.data.message);
      setEmail("");
    } catch {
      setSubStatus("Something went wrong. Please try again.");
    }
  };

  return (
    <footer className="bg-[#060606] border-t border-white/5">
      {/* Top bar */}
      <div className="border-b border-white/5 py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="font-display text-2xl text-[#FAF8F3] mb-1">
                Stay Ahead of the Market
              </h3>
              <p className="text-[#A0A0A0] text-sm font-light">
                Exclusive infrastructure updates & investment opportunities
              </p>
            </div>
            <form
              onSubmit={handleNewsletter}
              data-testid={HOME.newsletterForm}
              className="flex gap-0 w-full md:w-auto"
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="input-luxury border-b border-[#D4AF37]/30 focus:border-[#D4AF37] px-4 py-3 min-w-[260px] text-sm"
                required
              />
              <button type="submit" className="btn-gold py-3 px-6 text-xs ml-3 shrink-0">
                Subscribe <ArrowRight size={12} className="inline ml-1" />
              </button>
            </form>
            {subStatus && (
              <p className="text-[#D4AF37] text-sm mt-2 md:absolute md:bottom-0">{subStatus}</p>
            )}
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6">
              <img
                src="/sneha-logo.jpg"
                alt="Sneha Reality"
                className="h-16 w-auto object-contain"
                style={{ filter: "drop-shadow(0 0 12px rgba(212,175,55,0.2))" }}
              />
            </Link>
            <p className="text-[#666] text-sm font-light leading-relaxed mb-6 max-w-xs">
              We don't sell property. We engineer wealth through strategic real estate investments in Navi Mumbai &amp; Panvel.
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-3 text-sm text-[#A0A0A0]">
                <MapPin size={14} className="text-[#D4AF37] shrink-0" />
                <span>Kharghar, Navi Mumbai, Maharashtra</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-[#A0A0A0]">
                <Phone size={14} className="text-[#D4AF37] shrink-0" />
                <a href="tel:+917977009190" className="hover:text-[#D4AF37] transition-colors">+91 7977009190</a>
              </div>
              <div className="flex items-center gap-3 text-sm text-[#A0A0A0]">
                <Mail size={14} className="text-[#D4AF37] shrink-0" />
                <a href="mailto:info@snehareality.com" className="hover:text-[#D4AF37] transition-colors">info@snehareality.com</a>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-6">
              {[Instagram, Youtube, Twitter].map((Icon, i) => (
                <a key={i} href="#" className="w-8 h-8 border border-white/10 flex items-center justify-center text-[#666] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300">
                  <Icon size={14} />
                </a>
              ))}
            </div>
          </div>

          {/* Links */}
          {Object.entries(footerLinks).map(([title, links]) => (
            <div key={title}>
              <p className="section-label text-[10px] mb-5">{title}</p>
              <ul className="space-y-3">
                {links.map((l) => (
                  <li key={l.label}>
                    <Link
                      to={l.href}
                      className="text-sm text-[#666] hover:text-[#D4AF37] transition-colors font-light"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/5 py-6">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[#444] text-xs font-light">
            © 2025 Sneha Reality. All rights reserved. RERA Registered.
          </p>
          <p className="text-[#333] text-xs font-light italic">
            "We Don't Sell Property. We Engineer Wealth."
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
